
import React, { memo } from "react";
import { Bot, User } from "lucide-react";
import FeedbackButtons from "./FeedbackButtons";
import ReactMarkdown from 'react-markdown';

export interface Message {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
}

interface ChatMessageProps {
  message: Message;
  index: number;
  onFeedbackSubmit?: (messageId: number, helpful: boolean) => Promise<void>;
}

// Memoize individual message components for better performance
const ChatMessage = memo(({ message, index, onFeedbackSubmit }: ChatMessageProps) => (
  <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`flex flex-col ${message.role === "user" ? "items-end" : "items-start"}`}>
      <div className={`flex items-start gap-2 max-w-[80%] ${message.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"} p-3 rounded-lg`}>
        <div className="mt-0.5">
          {message.role === "user" ? <User className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
        </div>
        <div className="break-words prose-sm dark:prose-invert text-left">
          {message.role === "assistant" ? (
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h3 className="text-lg font-bold mt-0 mb-2 text-left">{children}</h3>,
                h2: ({ children }) => <h4 className="text-md font-semibold mt-0 mb-1 text-left">{children}</h4>,
                p: ({ children }) => <p className="mb-2 text-left">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-5 mb-2 text-left">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 text-left">{children}</ol>,
                li: ({ children }) => <li className="mb-1 text-left">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-left">{children}</strong>,
                blockquote: ({ children }) => <blockquote className="border-l-2 border-primary/20 pl-2 italic my-2 text-left">{children}</blockquote>,
              }}
            >
              {message.content}
            </ReactMarkdown>
          ) : (
            message.content
          )}
        </div>
      </div>
      
      {/* Only show feedback buttons for assistant messages that are not the intro message (index 0) */}
      {message.role === "assistant" && index > 0 && onFeedbackSubmit && message.id !== undefined && (
        <FeedbackButtons 
          messageId={message.id} 
          onFeedbackSubmit={onFeedbackSubmit}
          messageRole={message.role}
        />
      )}
    </div>
  </div>
));

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
